import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { categoryRunAction, setCategoryDefault, errorMessage } from "./revenueApi";
import { Progress } from "./parts";
import { RUN_ACTION_LABELS, RUN_STATUS, dateOnly, dateTime, duration, fmt, compact } from "./format";

// What each button does, in plain words, for the confirmation dialog.
const ACTIONS = {
  start: {
    label: "Start",
    cls: "success",
    title: "Start this category?",
    text: "From now on its users' gifts pay the category 10% + 20% pending, and count toward its goal. A new cycle starts from zero today.",
  },
  resume: {
    label: "Resume",
    cls: "success",
    title: "Resume this category?",
    text: "The paused cycle continues where it stopped. Its end date moves forward by the time it was paused.",
  },
  pause: {
    label: "Pause",
    cls: "ghost",
    title: "Pause this category?",
    text: "The cycle freezes: its time stops and its held coins stay. Until you resume, its users' gifts are split between the user and the app only.",
  },
  stop: {
    label: "Stop",
    cls: "danger",
    title: "Stop this category?",
    text: "The current cycle ends now. Nothing counts until you start it again, and the next start begins a new cycle from zero.",
  },
};

/// Live seconds-left: ticks every second while the category is running;
/// frozen while paused (the server already froze it).
function useCountdown(cycle, running) {
  const [left, setLeft] = useState(cycle?.seconds_left ?? 0);
  useEffect(() => {
    setLeft(cycle?.seconds_left ?? 0);
    if (!running || !cycle) return undefined;
    const started = Date.now();
    const base = cycle.seconds_left ?? 0;
    const t = setInterval(() => setLeft(Math.max(0, base - Math.floor((Date.now() - started) / 1000))), 1000);
    return () => clearInterval(t);
  }, [cycle, running]);
  return left;
}

/// Start / Pause / Resume / Stop for one target category, with its clock.
/// `run` is the backend's runInfo (or the list row's run fields); `compact`
/// renders a one-line version for the category list.
export default function RunControl({ categoryId, run, onChanged, compact: isCompact = false, showDefaultToggle = false }) {
  const [confirm, setConfirm] = useState(null); // action key
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const status = run?.run_status || "stopped";
  const isDefault = !!run?.is_default;
  const cycle = run?.current_cycle || null;
  const can = useMemo(
    () =>
      run?.can || {
        start: !isDefault && status === "stopped",
        pause: status === "running",
        resume: !isDefault && status === "paused",
        stop: status !== "stopped",
      },
    [run, isDefault, status]
  );
  const left = useCountdown(cycle, status === "running");
  const st = RUN_STATUS[status] || RUN_STATUS.stopped;

  const doAction = async () => {
    setBusy(true);
    setError("");
    try {
      await categoryRunAction(categoryId, confirm, note.trim() || undefined);
      setConfirm(null);
      setNote("");
      onChanged && onChanged();
    } catch (e) {
      setError(errorMessage(e, "Could not change the status"));
    } finally {
      setBusy(false);
    }
  };

  const toggleDefault = async () => {
    setBusy(true);
    setError("");
    try {
      await setCategoryDefault(categoryId, !isDefault);
      onChanged && onChanged();
    } catch (e) {
      setError(errorMessage(e, "Could not change the default flag"));
    } finally {
      setBusy(false);
    }
  };

  const buttons = ["start", "resume", "pause", "stop"].filter((k) => can[k]);

  const heldNow = cycle && cycle.status === "running" ? Number(cycle.pending_coins || 0) : 0;

  // Portal to <body>: the category list sits inside animated (transformed)
  // wrappers, which would otherwise trap a position:fixed dialog inside them.
  const dialog = confirm && createPortal(
    <div className="rv-modal-backdrop" onClick={() => !busy && setConfirm(null)}>
      <div className="rv-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rv-panel-title mb-2">{ACTIONS[confirm].title}</div>
        <p className="text-grey mb-3" style={{ lineHeight: 1.6 }}>
          {ACTIONS[confirm].text}
        </p>
        {confirm === "stop" && heldNow > 0 && (
          <div className="rv-error mb-3">
            The goal isn't reached yet: the <b>{fmt(heldNow)}</b> coins held for this cycle will go to the app.
          </div>
        )}
        {confirm === "stop" && cycle?.status === "achieved" && (
          <div className="rv-success mb-3">The goal was already reached this cycle — nothing is lost.</div>
        )}
        <label className="rv-label">Note (optional)</label>
        <input className="rv-input mb-3" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Why? (shown in the history)" />
        {error && <div className="rv-error mb-3">{error}</div>}
        <div className="d-flex gap-2 justify-content-end">
          <button type="button" className="rv-btn ghost" disabled={busy} onClick={() => setConfirm(null)}>
            Cancel
          </button>
          <button type="button" className={`rv-btn ${ACTIONS[confirm].cls}`} disabled={busy} onClick={doAction}>
            {busy ? "Saving…" : ACTIONS[confirm].label}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

  if (isCompact) {
    return (
      <div className="rv-run-compact" onClick={(e) => e.stopPropagation()}>
        <span className={`rv-badge ${st.cls}`}>
          <span className="rv-dot" style={{ background: st.dot, width: 8, height: 8 }} />
          {isDefault ? "Default · no revenue" : st.label}
        </span>
        {!isDefault && cycle && (
          <span className="rv-run-meta">
            {compact(cycle.achieved_coins)} / {compact(cycle.goal_coins)}
            {" · "}
            {status === "paused" ? `paused, ${duration(left)} left` : `${duration(left)} left`}
          </span>
        )}
        {buttons.map((k) => (
          <button key={k} type="button" className={`rv-btn rv-btn-sm ${ACTIONS[k].cls}`} onClick={() => setConfirm(k)}>
            {ACTIONS[k].label}
          </button>
        ))}
        {error && !confirm && <span className="rv-run-error">{error}</span>}
        {dialog}
      </div>
    );
  }

  return (
    <div className="rv-panel h-100">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="rv-panel-title mb-0">Category status</div>
        <span className={`rv-badge ${st.cls}`} style={{ fontSize: "0.85rem" }}>
          <span className="rv-dot" style={{ background: st.dot, width: 8, height: 8 }} />
          {st.label}
        </span>
      </div>

      {isDefault ? (
        <div className="rv-empty py-2">
          This is your default category (no account). Its users have targets for analytics only — it never takes a
          revenue share and can't be started.
        </div>
      ) : status === "stopped" ? (
        <div className="text-grey mb-3" style={{ lineHeight: 1.6 }}>
          Nothing is counted while the category is stopped: its users' gifts are split between the user and the app
          only. Press <b className="text-white">Start</b> to begin a {run?.category?.cycle_months || 1}-month cycle
          toward {fmt(run?.category?.cycle_goal_coins)} coins.
          {run?.last_cycle && (
            <div className="small mt-2">
              Last cycle #{run.last_cycle.cycle_no}: {fmt(run.last_cycle.achieved_coins)} / {fmt(run.last_cycle.goal_coins)} ·{" "}
              {dateOnly(run.last_cycle.starts_at)} → {dateOnly(run.last_cycle.ends_at)}
            </div>
          )}
        </div>
      ) : cycle ? (
        <>
          <div className="d-flex justify-content-between align-items-end mb-2">
            <div>
              <div className="text-grey small">Collected this cycle</div>
              <div className="rv-kpi-value mb-0">{fmt(cycle.achieved_coins)}</div>
              <div className="text-grey small">of {fmt(cycle.goal_coins)} coins · {cycle.progress}%</div>
            </div>
            <div className="text-end">
              <div className="text-grey small">{status === "paused" ? "Time left (frozen)" : "Time left"}</div>
              <div className="fw-bold fs-4" style={{ fontVariantNumeric: "tabular-nums" }}>
                {duration(left)}
              </div>
              <div className="text-grey small">ends {dateTime(cycle.ends_at)}</div>
            </div>
          </div>
          <Progress value={cycle.progress} />
          <div className="rv-run-time mt-2">
            <span style={{ width: `${cycle.time_percent || 0}%` }} />
          </div>
          <div className="d-flex justify-content-between text-grey small mt-1">
            <span>Started {dateTime(run.run_started_at || cycle.starts_at)}</span>
            <span>{cycle.time_percent}% of the month used</span>
          </div>
          <div className="rv-stat-row mt-2">
            <span>Cycle</span>
            <span>
              #{cycle.cycle_no} · {cycle.status === "achieved" ? "goal reached" : "goal not reached yet"}
            </span>
          </div>
          <div className="rv-stat-row">
            <span>Held (pending) coins</span>
            <span>{fmt(cycle.pending_coins)}</span>
          </div>
          <div className="rv-stat-row">
            <span>Released to the category</span>
            <span>{fmt(cycle.released_coins)}</span>
          </div>
          {cycle.paused_seconds > 0 && (
            <div className="rv-stat-row">
              <span>Paused so far</span>
              <span>{duration(cycle.paused_seconds)}</span>
            </div>
          )}
        </>
      ) : (
        <div className="rv-empty py-2">No open cycle.</div>
      )}

      {(buttons.length > 0 || showDefaultToggle) && (
        <div className="d-flex flex-wrap gap-2 mt-3">
          {buttons.map((k) => (
            <button key={k} type="button" className={`rv-btn ${ACTIONS[k].cls}`} onClick={() => setConfirm(k)}>
              {ACTIONS[k].label}
            </button>
          ))}
          {showDefaultToggle && (
            <button type="button" className="rv-btn ghost ms-auto" disabled={busy} onClick={toggleDefault} title="The admin's own category: analytics only, no revenue share, no login">
              {isDefault ? "Remove default flag" : "Make default (no revenue)"}
            </button>
          )}
        </div>
      )}
      {error && !confirm && <div className="rv-error mt-3">{error}</div>}
      {dialog}
    </div>
  );
}

/// The Start / Pause / Resume / Stop history.
export function RunLog({ log = [] }) {
  if (!log.length) return <div className="rv-empty">No changes yet.</div>;
  return (
    <div className="rv-table-wrap">
      <table className="rv-table" style={{ minWidth: 520 }}>
        <thead>
          <tr>
            <th>When</th>
            <th>What</th>
            <th>By</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {log.map((l) => (
            <tr key={l.id}>
              <td className="small text-grey">{dateTime(l.created_at)}</td>
              <td>
                <span className={`rv-badge ${RUN_STATUS[l.to_status]?.cls || "rv-badge-grey"}`}>{RUN_ACTION_LABELS[l.action] || l.action}</span>
              </td>
              <td className="small">{l.actor_type === "admin" ? "Admin" : "Automatic"}</td>
              <td className="small text-grey">
                {l.details?.forfeited_coins > 0 && <>Held {fmt(l.details.forfeited_coins)} → app. </>}
                {l.details?.paused_seconds > 0 && <>Paused for {duration(l.details.paused_seconds)}. </>}
                {l.details?.previous_cycle_status && <>Previous cycle {l.details.previous_cycle_status}. </>}
                {l.note}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
